import { inject, Injectable, signal } from '@angular/core';
import { LaunchDarklyFlag, LaunchDarklyService } from '@anduin/auth';
import { ConfigService, PmSystem } from '@anduin/config';
import { buildActiveFilters } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { EmailDeliveryTab } from '../models/email-delivery-tab.enum';
import { EmailDeliveryHubState } from '../state';
import { injectEmailDeliveryTracking } from './email-delivery-tracking.providers';
import {
  EmailDeliveryBaseMetadata,
  EmailDeliveryStepMetadataInput,
  EmailDeliveryTabLabel,
  EmailDeliveryWorkflowStep,
  EmailDeliveryWorkflowStepMetaDataMap,
} from './email-delivery-workflow.models';

export const TAB_LABELS: Record<EmailDeliveryTab, EmailDeliveryTabLabel> = {
  [EmailDeliveryTab.SENT]: 'SENT',
  [EmailDeliveryTab.PENDING]: 'PENDING',
  [EmailDeliveryTab.FAILED]: 'FAILED',
};

/**
 * Per-container tracking facade for Email Delivery Hub. Wraps `TrackWorkflowService` and
 * stamps `EmailDeliveryBaseMetadata` onto every event so call sites only pass step extras.
 * Registered on `EmailDeliveryHubContainerComponent` (`providers: [...]`) so child tab
 * tables and the resend modal inherit a single instance via the element injector.
 *
 *   - `activeTab` — derived from `EmailDeliveryHubState.getActiveTab`. Tab-transition flows
 *     fire `EMAIL_DELIVERY_TAB_SWITCHED` BEFORE dispatching `SetActiveTab` so the resolved
 *     `activeTab` matches the outgoing (from) tab.
 *   - `pmsSystem` — `ConfigService.pmSystem`.
 *   - `failedTabAvailable` — `ENABLE_UNSENT_EMAIL_TABS` AND `pmSystem === CCH_AXCESS`.
 *   - `activeFilters` — comma-bracketed token list resolved from setter-driven search term
 *     and column-filter model (fed in from `EmailDeliveryHubFiltersComponent`).
 */
@Injectable()
export class EmailDeliveryTrackService {
  private readonly tracker = injectEmailDeliveryTracking();
  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  private readonly _unsentEmailTabsEnabled = this.launchDarklyService.getFeatureFlagSignal(LaunchDarklyFlag.ENABLE_UNSENT_EMAIL_TABS);

  private readonly _searchValue = signal<string>('');
  private readonly _columnFilters = signal<Record<string, unknown>>({});

  setSearchValue(value: string | null | undefined): void {
    this._searchValue.set(value ?? '');
  }

  setColumnFilters(model: Record<string, unknown> | null | undefined): void {
    this._columnFilters.set(model ?? {});
  }

  trackWorkflow<E extends EmailDeliveryWorkflowStep>(step: E, metadata?: EmailDeliveryStepMetadataInput<E>): void {
    const merged = { ...this.resolveBaseMetadata(), ...(metadata ?? {}) } as EmailDeliveryWorkflowStepMetaDataMap[E];
    this.tracker.trackWorkflow(step, merged);
  }

  private resolveBaseMetadata(): EmailDeliveryBaseMetadata {
    return {
      activeTab: this.resolveActiveTab(),
      pmsSystem: this.configService.pmSystem ?? 'UNKNOWN',
      failedTabAvailable: this.resolveFailedTabAvailable(),
      activeFilters: this.resolveActiveFilters(),
    };
  }

  private resolveActiveTab(): EmailDeliveryTabLabel {
    const tab = this.store.selectSnapshot(EmailDeliveryHubState.getActiveTab) ?? EmailDeliveryTab.SENT;
    return TAB_LABELS[tab];
  }

  private resolveFailedTabAvailable(): boolean {
    return this._unsentEmailTabsEnabled() && this.configService.pmSystem === PmSystem.CCH_AXCESS;
  }

  private resolveActiveFilters(): string {
    const tokens: string[] = [];
    if (this._searchValue().trim().length > 0) {
      tokens.push('search');
    }
    for (const [columnId, model] of Object.entries(this._columnFilters())) {
      if (model != null) {
        tokens.push(`column:${columnId}`);
      }
    }
    return buildActiveFilters(tokens);
  }
}
